// (sdk ADR-b0123, sessions ADR-9166f)

export interface QueuedImageBackend {
  put(key: string, blob: Blob): Promise<void>;
  get(key: string): Promise<Blob | null>;
  delete(key: string): Promise<void>;
  keys(prefix: string): Promise<string[]>;
}

export class QueuedImageStoreError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = 'QueuedImageStoreError';
  }
}

interface IdbRequestLike<T> {
  readonly result: T;
  readonly error: unknown;
  onsuccess: (() => void) | null;
  onerror: (() => void) | null;
}

interface IdbOpenRequestLike extends IdbRequestLike<IdbDatabaseLike> {
  onupgradeneeded: (() => void) | null;
}

export interface IdbFactoryLike {
  open(name: string, version: number): IdbOpenRequestLike;
}

interface IdbDatabaseLike {
  transaction(store: string, mode: 'readonly' | 'readwrite'): IdbTransactionLike;
  createObjectStore(name: string): unknown;
  close(): void;
  onversionchange: (() => void) | null;
}

interface IdbTransactionLike {
  objectStore(name: string): IdbObjectStoreLike;
  readonly error: unknown;
  oncomplete: (() => void) | null;
  onabort: (() => void) | null;
}

interface IdbObjectStoreLike {
  readonly transaction: IdbTransactionLike;
  put(value: unknown, key: string): IdbRequestLike<unknown>;
  get(key: string): IdbRequestLike<unknown>;
  delete(key: string): IdbRequestLike<unknown>;
  getAllKeys(): IdbRequestLike<unknown[]>;
}

const asError = (err: unknown, fallback: string): Error => (err instanceof Error ? err : new Error(fallback));

const DB_NAME = 'localagent-queued-images';
const STORE = 'images';

function request<T>(req: IdbRequestLike<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(asError(req.error, 'IndexedDB request failed'));
  });
}

export class IndexedDbQueuedImageBackend implements QueuedImageBackend {
  private db: Promise<IdbDatabaseLike> | null = null;

  constructor(private readonly factory: IdbFactoryLike) {}

  private open(): Promise<IdbDatabaseLike> {
    if (!this.db) {
      this.db = new Promise((resolve, reject) => {
        const req = this.factory.open(DB_NAME, 1);
        req.onupgradeneeded = () => {
          req.result.createObjectStore(STORE);
        };
        req.onsuccess = () => {
          const db = req.result;
          db.onversionchange = () => {
            db.close();
            this.db = null;
          };
          resolve(db);
        };
        req.onerror = () => {
          this.db = null;
          reject(asError(req.error, 'IndexedDB open failed'));
        };
      });
    }
    return this.db;
  }

  private async tx(mode: 'readonly' | 'readwrite'): Promise<IdbObjectStoreLike> {
    return (await this.open()).transaction(STORE, mode).objectStore(STORE);
  }

  async put(key: string, blob: Blob): Promise<void> {
    const store = await this.tx('readwrite');
    await new Promise<void>((resolve, reject) => {
      const req = store.put(blob, key);
      req.onerror = () => reject(asError(req.error, 'IndexedDB put failed'));
      store.transaction.oncomplete = () => resolve();
      store.transaction.onabort = () => reject(asError(store.transaction.error, 'IndexedDB write aborted'));
    });
  }

  async get(key: string): Promise<Blob | null> {
    const value = await request((await this.tx('readonly')).get(key));
    return value instanceof Blob ? value : null;
  }

  async delete(key: string): Promise<void> {
    await request((await this.tx('readwrite')).delete(key));
  }

  async keys(prefix: string): Promise<string[]> {
    const all = await request((await this.tx('readonly')).getAllKeys());
    return all.map(String).filter((k) => k.startsWith(prefix));
  }
}

export class QueuedImageStore {
  private readonly pending = new Set<string>();

  constructor(private readonly backend: QueuedImageBackend | null) {}

  private static key(sessionId: string, imageId: string): string {
    return `${sessionId}/${imageId}`;
  }

  /** Keep an uploaded image's bytes; rejects when this device cannot. */
  async keep(sessionId: string, imageId: string, blob: Blob): Promise<void> {
    if (!this.backend) {
      throw new QueuedImageStoreError('This device has no storage for queued images.');
    }
    try {
      await this.backend.put(QueuedImageStore.key(sessionId, imageId), blob);
    } catch (err) {
      throw new QueuedImageStoreError("This device can't keep a copy of the image.", err);
    }
    this.pending.add(imageId);
  }

  /** The image no longer waits in a composer: it was sent or removed. */
  settle(imageId: string): void {
    this.pending.delete(imageId);
  }

  async get(sessionId: string, imageId: string): Promise<Blob | null> {
    if (!this.backend) return null;
    return this.backend.get(QueuedImageStore.key(sessionId, imageId));
  }

  /** Drop every kept image of `sessionId` that `keep` rejects and no composer still holds. */
  async release(sessionId: string, keep: (imageId: string) => boolean): Promise<void> {
    if (!this.backend) return;
    const prefix = `${sessionId}/`;
    for (const key of await this.backend.keys(prefix)) {
      const imageId = key.slice(prefix.length);
      if (!this.pending.has(imageId) && !keep(imageId)) await this.backend.delete(key);
    }
  }

  /** Forget every kept image, e.g. on sign-out. */
  async clear(): Promise<void> {
    this.pending.clear();
    if (!this.backend) return;
    for (const key of await this.backend.keys('')) await this.backend.delete(key);
  }
}

export function defaultQueuedImageBackend(): QueuedImageBackend | null {
  const factory = (globalThis as { indexedDB?: IdbFactoryLike }).indexedDB;
  return factory ? new IndexedDbQueuedImageBackend(factory) : null;
}
